/**
 * Design-system tokens — frozen DESIGN.md frontmatter (revision 4).
 * EF Track A Story A3: full token surface (colors + type + spacing + motion + density).
 * Do not invent new keys in feature work (ADR-003). Stack* components (A4) consume these only.
 */

/** @param {number} px */
export function pxToRem(px) {
  return `${px / 16}rem`;
}

export const colors = {
  canvas: '#0B0C0E',
  'canvas-light': '#FFFFFF',
  recessed: '#15181C',
  'recessed-light': '#F1F3F6',
  panel: '#1A1E23',
  'panel-light': '#E9ECF0',
  chrome: '#20242B',
  'chrome-light': '#E1E5EA',
  soft: '#272C34',
  'soft-light': '#D8DDE3',
  hair: '#282D35',
  'hair-light': '#D5DAE0',
  edge: '#70757E',
  'edge-light': '#767C85',
  'text-primary': '#EDEEF0',
  'text-primary-light': '#16191D',
  'text-secondary': '#B4B8C0',
  'text-secondary-light': '#4A515B',
  'text-muted': '#9AA0A9',
  'text-muted-light': '#515861',
  'text-disabled': '#5F636B',
  'text-disabled-light': '#9AA0A9',
  accent: '#8F93FF',
  'accent-light': '#4045C9',
  'accent-surface': '#262A45',
  'accent-surface-light': '#E6E7FB',
  'accent-edge': '#8F93FF',
  'accent-edge-light': '#4045C9',
  'focus-inner': '#0B0C0E',
  'focus-inner-light': '#FFFFFF',
  'focus-outer': '#EDEEF0',
  'focus-outer-light': '#16191D',
  'status-crit': '#F0666B',
  'status-crit-light': '#B3242A',
  'status-warn': '#E0B36A',
  'status-warn-light': '#7D5407',
  'status-ok': '#54CE9C',
  'status-ok-light': '#0C6544',
  'status-info': '#6BAEFF',
  'status-info-light': '#1856B3',
  'data-1': '#5FA8F5',
  'data-1-light': '#1A6FD4',
  'data-2': '#4FC79A',
  'data-2-light': '#127A55',
  'data-3': '#DFB05F',
  'data-3-light': '#8A6008',
  'data-4': '#B39BFB',
  'data-4-light': '#6B4FD6',
  'data-5': '#4FD6C9',
  'data-5-light': '#0F7A72',
  'data-6': '#F2907A',
  'data-6-light': '#C04A2E',
  'heat-0': '#141719',
  'heat-1': '#1C3A2C',
  'heat-2': '#245A3F',
  'heat-3': '#2F8A5C',
  'heat-4': '#43BE8E',
  'heat-0-light': '#EFF1F3',
  'heat-1-light': '#CDE9DA',
  'heat-2-light': '#8FCFB0',
  'heat-3-light': '#3F9E74',
  'heat-4-light': '#10704D',
  scrim: 'rgba(3, 4, 6, 0.72)',
  'scrim-light': 'rgba(22, 25, 29, 0.44)',
};

/** Authored in px per DESIGN.md; theme emits rem via pxToRem. */
export const typography = {
  display: {
    fontFamily: 'Inter',
    fontSizePx: 30,
    fontWeight: 600,
    lineHeight: 1.1,
    letterSpacing: '-0.03em',
  },
  h1: {
    fontFamily: 'Inter',
    fontSizePx: 19,
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-0.022em',
  },
  h2: {
    fontFamily: 'Inter',
    fontSizePx: 15,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.015em',
  },
  h3: {
    fontFamily: 'Inter',
    fontSizePx: 13.5,
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  body: {
    fontFamily: 'Inter',
    fontSizePx: 13.5,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  'body-sm': {
    fontFamily: 'Inter',
    fontSizePx: 12.5,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontFamily: 'Inter',
    fontSizePx: 11.5,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  micro: {
    fontFamily: 'Inter',
    fontSizePx: 10.5,
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.08em',
  },
  nano: {
    fontFamily: 'Inter',
    fontSizePx: 9.5,
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.12em',
  },
  metric: {
    fontFamily: 'Inter',
    fontSizePx: 24,
    fontWeight: 600,
    lineHeight: 1.1,
    letterSpacing: '-0.03em',
  },
  numeric: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSizePx: 12,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  'numeric-sm': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSizePx: 11,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  kbd: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSizePx: 10.5,
    fontWeight: 500,
    lineHeight: 1,
  },
};

export const rounded = {
  none: 0,
  DEFAULT: 0,
  sm: 3,
  md: 5,
  lg: 6,
};

/**
 * 4-based design scale (px) — consume via theme.ef.spacing / tokens.spacing.
 * MUI theme.spacing stays at the default 8px unit (A3 D1); a true 4px MUI
 * spacing system requires a dedicated Track B codemod across call sites.
 */
export const spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
  gutter: 26,
  'content-x': 22,
  'content-y': 20,
  'panel-x': 13,
  'panel-y': 10,
  measure: '78ch',
};

export const motion = {
  instant: 0,
  fast: 120,
  base: 200,
  slow: 320,
  ease: 'cubic-bezier(0.2, 0, 0.2, 1)',
  'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
};

/** Role density — pointer surfaces. Touch floors at row-touch (44px). */
export const density = {
  'row-admin': 26,
  'row-hod': 26,
  'row-faculty': 30,
  'row-cbc': 32,
  'row-student': 36,
  'row-touch': 44,
  'shell-bar': 32,
  'status-bar': 24,
};

/** MUI five defaults unchanged; xxl/xxxl added. Supply values only (IC-3). */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
  xxl: 2160,
  xxxl: 2560,
};

/** Surface ladder used for "worst of 5 surfaces" contrast basis. */
export const SURFACE_KEYS = ['canvas', 'recessed', 'panel', 'chrome', 'soft'];

export const STACK_COMPONENT_INVENTORY = {
  shell: [
    'StackActivityBar',
    'StackExplorer',
    'StackTabStrip',
    'StackStatusBar',
    'StackInspector',
    'StackReferenceColumn',
  ],
  content: [
    'StackPanel',
    'StackSection',
    'StackPageHeader',
    'StackCard',
    'StackMetric',
    'StackTable',
    'StackDataGrid',
    'StackBadge',
    'StackTimeline',
    'StackChart',
  ],
  interaction: [
    'StackFormField',
    'StackButton',
    'StackCommandPalette',
    'StackDialog',
    'StackDrawer',
  ],
  state: [
    'StackLoadingState',
    'StackEmptyState',
    'StackErrorState',
    'StackPartialState',
  ],
};

export function allStackComponentNames() {
  return Object.values(STACK_COMPONENT_INVENTORY).flat();
}

/** Resolve palette color for mode (`dark` uses base key; `light` uses `-light` suffix). */
export function colorForMode(tokenKey, mode) {
  if (mode === 'light') {
    const lightKey = `${tokenKey}-light`;
    if (colors[lightKey]) return colors[lightKey];
  }
  return colors[tokenKey];
}

/**
 * Map auth role string → density row token key.
 * @param {string | null | undefined} role
 */
export function densityKeyForRole(role) {
  const r = String(role || '').toLowerCase();
  if (r.includes('admin')) return 'row-admin';
  if (r.includes('hod')) return 'row-hod';
  if (r.includes('cbc')) return 'row-cbc';
  if (r.includes('student')) return 'row-student';
  if (r.includes('faculty')) return 'row-faculty';
  return 'row-faculty';
}

export default {
  colors,
  typography,
  rounded,
  spacing,
  motion,
  density,
  breakpoints,
  SURFACE_KEYS,
  STACK_COMPONENT_INVENTORY,
  allStackComponentNames,
  pxToRem,
  colorForMode,
  densityKeyForRole,
};
